import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

// Google Maps URLs - Clock Tower Offices - Dimas, Sharjah
// Office: Floor 9, Office 901
// Address: 996W+2F2 - Al Mussalla - Hay Al Gharb - Sharjah
const GOOGLE_MAPS_LINK = 'https://www.google.com/maps/search/Clock+Tower+Offices+Dimas+Sharjah';
const GOOGLE_MAPS_DIRECTIONS = 'https://www.google.com/maps/dir//Clock+Tower+Offices+Dimas,+Al+Mussalla,+Hay+Al+Gharb,+Sharjah,+UAE';

// Embed URL - Clock Tower Offices - Dimas, Sharjah (Al Mussalla - Hay Al Gharb)
const GOOGLE_MAPS_EMBED = 'https://maps.google.com/maps?q=Clock+Tower+Offices+Dimas+Sharjah&t=&z=16&ie=UTF8&iwloc=&output=embed';

const MapSection = ({ className = '' }) => {
  const { t, isRTL } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleMediaChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    // Intersection Observer for fade-in animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openInGoogleMaps = () => {
    window.open(GOOGLE_MAPS_LINK, '_blank');
  };

  const getDirections = () => {
    window.open(GOOGLE_MAPS_DIRECTIONS, '_blank');
  };

  return (
    <section 
      ref={sectionRef}
      className={`py-16 md:py-20 bg-background ${className}`}
    >
      <div className="container-custom">
        <div 
          className={`transition-all duration-1000 ease-out ${
            prefersReducedMotion 
              ? '' 
              : isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-uae-red/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <MapPin className="w-4 h-4 text-uae-red" />
              <span className="text-sm font-medium text-uae-red">{t('map.ourLocation')}</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('map.findUs')}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t('map.floor')}
            </p>
          </div>

          {/* Map Card */}
          <Card className="bg-card border-border overflow-hidden shadow-premium max-w-5xl mx-auto">
            <CardContent className="p-0">
              {/* Map Container */}
              <div className="relative w-full h-[260px] sm:h-[320px] md:h-[400px] bg-muted">
                {/* Lazy-loaded Google Maps Iframe */}
                <iframe
                  src={GOOGLE_MAPS_EMBED}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TDABYR ALMSTQBL Office Location"
                  className="absolute inset-0 w-full h-full"
                />
                
                {/* Gradient Overlay at bottom for smooth transition */}
                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="p-6 bg-card">
                <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <Button
                    variant="uaeRed"
                    size="lg"
                    className="w-full sm:w-auto gap-2"
                    onClick={openInGoogleMaps}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('map.openMaps')}
                  </Button>
                  <Button
                    variant="outlineGreen"
                    size="lg"
                    className="w-full sm:w-auto gap-2"
                    onClick={getDirections}
                  >
                    <Navigation className="w-4 h-4" />
                    {t('map.getDirections')}
                  </Button>
                </div>

                {/* Office Details */}
                <div className={`flex items-center justify-center gap-6 mt-6 pt-6 border-t border-border/50 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-8 h-8 rounded-lg bg-uae-green/10 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-uae-green" />
                    </div>
                    <span className="text-sm">{t('map.sharjah')}</span>
                  </div>
                  <div className="w-px h-6 bg-border" />
                  <div className="text-sm text-muted-foreground">
                    <span className="text-foreground font-medium">{t('map.floorOffice')}</span> • {t('map.office')}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
